import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { GetHeaderBannersRequest, WebHeaderBannerResponse } from 'src/apis/dto/DTOHeaderBanner';
import useLogin from 'src/hooks/useLogin';
import { azarTokenAtom, userCountryCodeAtom } from 'src/stores/auth/atoms';
import { client, clientWithoutAuth } from 'src/utils/api';
import getDeviceId from 'src/utils/device/id';
import DeviceInfo, { getWebBrowserOs, getWebDisplayMode } from 'src/utils/device/info';
import { getServerSideLanguageCode } from 'src/utils/language';
import { safeWindow } from 'src/utils/static-generation';

const HEADER_BANNERS_PATH = '/api/v1/web/banners/header';

export const HEADER_BANNERS_QUERY_KEYS = {
  getHeaderBanners: () => [HEADER_BANNERS_PATH],
};

export const useHeaderBannersQuery = () => {
  const { isLogin } = useLogin();
  const token = useAtomValue(azarTokenAtom);
  const countryCode = useAtomValue(userCountryCodeAtom);

  const deviceInfo = safeWindow ? DeviceInfo() : undefined;
  const params: GetHeaderBannersRequest | undefined = deviceInfo
    ? {
        deviceId: getDeviceId(),
        flavor: deviceInfo.isMobile ? 'mobile' : 'desktop',
        localeLanguageCode: getServerSideLanguageCode(),
        localeCountryCode: countryCode,
        displayMode: getWebDisplayMode(),
        browserOs: getWebBrowserOs(),
      }
    : undefined;

  const apiClient = isLogin ? client : clientWithoutAuth;

  return useQuery({
    queryKey: [...HEADER_BANNERS_QUERY_KEYS.getHeaderBanners(), params, isLogin],
    queryFn: () => apiClient.get<WebHeaderBannerResponse>(HEADER_BANNERS_PATH, { params }),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !!params && (isLogin ? !!token : true),
  });
};

export type { HeaderBannerDto } from 'src/apis/dto/DTOHeaderBanner';
